import React, { useState } from "react";
import moment from "moment";
import { CronExpressionParser } from "cron-parser";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCompressArrowsAlt, faExpandArrowsAlt } from "@fortawesome/free-solid-svg-icons";
import { Button } from "aible";
import { isEnabled } from "utils/featureFlags";
import { useDispatch, useSelector } from "types/store";
import { changeDatasetName, getDataset, getDatasetOptionsSettings, saveDatasetOptions } from "containers/Home/reducer";
import { LineInput } from "components/Inputs/LineInput";
import LabeledToggle from "components/Inputs/Toggle/Toggle";
import GearIcon from "images/SenseIcons/cog-light 2.svg";
import styles from "./style.module.css";
import { getIsOpenDataValidation, getIsOpenLineage, getIsOpenSettings } from "containers/Sense/dataset-selectors";
import { updateTileState } from "containers/Sense/reducer";
import * as foldersReducer from "containers/Folders/reducer";
import TimezoneInput from "components/Tiles/Sense/TimezoneInput";
import CronExpressionInput from "components/Tiles/Sense/CronExpressionInput";
const RefreshIntervalInput = ({ datasetId }) => {
    const dispatch = useDispatch();
    const currentOptions = useSelector((state) => getDatasetOptionsSettings(state, datasetId));
    const refreshInterval = currentOptions?.refresh_interval;
    const [pendingRefreshInterval, updatePendingRefreshInterval] = useState(refreshInterval);
    const [isSaving, updateIsSaving] = useState(false);
    const defaultTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const hasPendingChanges = (refreshInterval === undefined && pendingRefreshInterval !== undefined) ||
        (refreshInterval !== undefined && pendingRefreshInterval === undefined) ||
        (refreshInterval !== undefined &&
            pendingRefreshInterval !== undefined &&
            (refreshInterval.cron_expr != pendingRefreshInterval.cron_expr ||
                refreshInterval.timezone != pendingRefreshInterval.timezone));
    const isDone = useSelector((state) => getDataset(state, datasetId)?.status_name === "DONE");
    const updatedDate = useSelector((state) => getDataset(state, datasetId)?.updated_date);
    const disableInputs = isSaving || !isDone;
    return (React.createElement("div", null,
        React.createElement("div", { className: styles.settingsItemFlex },
            React.createElement("div", { className: styles.settingsLabel, style: { marginRight: "auto" } }, "Enable Periodic Refresh"),
            React.createElement(LabeledToggle, { style: { display: "block" }, checked: pendingRefreshInterval !== undefined, onChange: () => updatePendingRefreshInterval(pendingRefreshInterval === undefined
                    ? {
                        cron_expr: "0 0 * * *",
                        timezone: defaultTimezone
                    }
                    : undefined), disabled: disableInputs, isAibleColors: true })),
        pendingRefreshInterval !== undefined && (React.createElement(React.Fragment, null,
            React.createElement("div", { className: styles.settingsItem },
                React.createElement("div", { className: styles.settingsLabel }, "Interval"),
                React.createElement("div", null,
                    React.createElement(CronExpressionInput, { value: pendingRefreshInterval.cron_expr, onChange: (cron_expr) => updatePendingRefreshInterval({
                            ...pendingRefreshInterval,
                            cron_expr
                        }), isDisabled: disableInputs }))),
            React.createElement("div", { className: styles.settingsItem },
                React.createElement("div", { className: styles.settingsLabel }, "Timezone"),
                React.createElement("div", null,
                    React.createElement(TimezoneInput, { value: pendingRefreshInterval.timezone ?? "UTC", onChange: (timezone) => updatePendingRefreshInterval({
                            ...pendingRefreshInterval,
                            timezone
                        }), menuPlacement: "top", isDisabled: disableInputs }))))),
        (pendingRefreshInterval !== undefined || hasPendingChanges) && (React.createElement("div", { className: styles.settingsActions },
            React.createElement("div", null,
                React.createElement(React.Fragment, null,
                    React.createElement("div", null, "Next Refresh"),
                    React.createElement("div", { className: styles.settingsSub }, pendingRefreshInterval === undefined
                        ? "Never"
                        : moment(CronExpressionParser.parse(pendingRefreshInterval.cron_expr, {
                            tz: pendingRefreshInterval.timezone,
                            currentDate: moment.utc(updatedDate).toDate()
                        })
                            .next()
                            .toISOString())
                            .local()
                            .format("MMMM Do YYYY, h:mm:ss a")))),
            React.createElement("div", null,
                React.createElement(Button, { style: { marginLeft: "10px" }, onClick: () => {
                        updatePendingRefreshInterval(currentOptions?.refresh_interval);
                    }, disabled: !hasPendingChanges || disableInputs, isLoading: isSaving }, "Reset"),
                React.createElement(Button, { style: { marginLeft: "10px" }, onClick: () => {
                        updateIsSaving(true);
                        dispatch(saveDatasetOptions(datasetId, {
                            ...currentOptions,
                            refresh_interval: pendingRefreshInterval
                        }, false)).finally(() => updateIsSaving(false));
                    }, disabled: !hasPendingChanges || disableInputs, isLoading: isSaving }, "Save Settings"))))));
};
const DatasetRename = ({ dataset }) => {
    const dispatch = useDispatch();
    const [isLoading, updateIsLoading] = useState(false);
    const [newName, updateNewName] = useState(dataset.name);
    const selectedFolder = useSelector((state) => foldersReducer.getSelectedFolder(state));
    const canEdit = useSelector((state) => foldersReducer.getUserEditPermission(state, selectedFolder));
    return (React.createElement("div", null,
        React.createElement("div", { className: styles.settingsSection },
            React.createElement("div", null, "Dataset Name"),
            React.createElement("div", { style: { width: "100%", marginBottom: "10px" } },
                React.createElement(LineInput, { isSenseBox: true, value: newName, onChange: (val) => updateNewName(val), isDisabled: !canEdit, style: { maxWidth: "unset" } })),
            React.createElement("div", { style: { display: "flex", width: "100%", justifyContent: "flex-end" } },
                React.createElement(Button, { style: { marginLeft: "auto" }, type: "secondary", onClick: () => {
                        updateIsLoading(true);
                        dispatch(changeDatasetName(dataset?.id, newName)).finally(() => updateIsLoading(false));
                    }, isLoading: isLoading }, "Update Name")),
            dataset.source.content.type == "CONNECTION_METADATA" && (React.createElement("div", { style: { marginTop: "20px" } },
                React.createElement(RefreshIntervalInput, { key: dataset.updated_date, datasetId: dataset.id })))),
        React.createElement("div", { style: { marginTop: "20px" } },
            React.createElement("div", null, "Created By"),
            React.createElement("div", { className: styles.settingsSub }, dataset?.created_by?.fullname +
                ", " +
                moment.utc(dataset.created_date).local().format("MMMM Do YYYY, h:mm:ss a")))));
};
const SettingsPanel = ({ datasetId }) => {
    const dispatch = useDispatch();
    const dataset = useSelector((state) => getDataset(state, datasetId));
    const isOpenSettings = useSelector((state) => getIsOpenSettings(state, datasetId));
    const isOpenDataValidation = useSelector((state) => getIsOpenDataValidation(state, datasetId));
    const isOpenLineage = useSelector((state) => getIsOpenLineage(state, datasetId));
    const updateIsOpenSettings = (isOpenSettings) => {
        dispatch(updateTileState(datasetId, { isOpenSettings }));
    };
    return (React.createElement("div", { className: isOpenSettings ? styles.openOverview : styles.overview, style: {
            maxHeight: isOpenSettings ? "800px" : "190px",
            width: isOpenSettings || (isOpenDataValidation && (isOpenLineage || !isEnabled("sense-transformations")))
                ? "100%"
                : (isOpenDataValidation && !isOpenLineage) ||
                    (!isOpenDataValidation && isOpenLineage) ||
                    !isEnabled("sense-transformations")
                    ? "calc(50% - 15px)"
                    : "calc(33.333% - 14px)",
            overflow: isOpenSettings ? "auto" : "hidden",
            marginLeft: isOpenSettings || isOpenDataValidation
                ? "10px"
                : isOpenLineage || !isEnabled("sense-transformations")
                    ? "0px"
                    : "0px"
        }, onClick: isOpenSettings !== true ? () => updateIsOpenSettings(!isOpenSettings) : undefined },
        React.createElement("div", { className: styles.senseTitle },
            React.createElement("div", { className: styles.sTitle },
                React.createElement("span", { className: styles.sIcon },
                    React.createElement("img", { src: GearIcon, style: { height: "16px", width: "16px" } })),
                React.createElement("span", null, "Settings")),
            isOpenSettings ? (React.createElement("div", { className: styles.closeButton, onClick: () => updateIsOpenSettings(!isOpenSettings) },
                React.createElement(Button, { style: { padding: "3px" } },
                    React.createElement("div", { className: styles.selIcon, title: "Close" },
                        React.createElement(FontAwesomeIcon, { icon: faCompressArrowsAlt }))))) : (React.createElement("div", { className: styles.selIcon + " " + styles.expandIcon },
                React.createElement(FontAwesomeIcon, { icon: faExpandArrowsAlt })))),
        React.createElement("div", { className: styles.tileFlex },
            React.createElement("div", { className: styles.tTop }, dataset?.created_by?.fullname),
            React.createElement("div", { className: styles.tBot }, "CREATED BY")),
        isOpenSettings && (React.createElement("div", { className: styles.dSettings },
            React.createElement(DatasetRename, { dataset: dataset })))));
};
export default SettingsPanel;
